import React from 'react';
import { Box, Typography, Container, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { images } from '../assets/images';
import { gradients } from '../theme';
import FadeIn from './FadeIn';

interface ServiceCardProps {
  title: string;
  description: string;
  image: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ title, description, image }) => (
  <Paper
    component={motion.div}
    whileHover={{ y: -8, borderColor: '#00f2ff' }}
    sx={{
      p: 4,
      height: '100%',
      minHeight: 320,
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 4,
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 20px 40px -10px rgba(0, 242, 255, 0.2)',
        '& .service-image': {
          transform: 'scale(1.05)',
        },
      },
    }}
  >
    {/* Title */}
    <Typography
      variant="h5"
      sx={{
        color: 'white',
        fontWeight: 700,
        mb: 2,
        lineHeight: 1.3,
        position: 'relative',
        zIndex: 1,
      }}
    >
      {title}
    </Typography>

    {/* Description */}
    <Typography
      sx={{
        color: 'grey.400',
        fontSize: '1rem',
        lineHeight: 1.6,
        mb: 3,
        maxWidth: '70%',
        position: 'relative',
        zIndex: 1,
      }}
    >
      {description}
    </Typography>

    {/* Image - Positioned at bottom right */}
    <Box
      component="img"
      src={image}
      alt={title}
      className="service-image"
      sx={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: 'auto',
        maxHeight: '85%',
        width: 'auto',
        maxWidth: '70%',
        objectFit: 'contain',
        transition: 'transform 0.4s ease',
      }}
    />
  </Paper>
);

const WhatWeDo: React.FC = () => {
  const services = [
    {
      title: 'Catalyst Intake & Logging',
      description: 'We receive used and scrap catalytic converters from garages, scrap dealers and fleets.',
      image: images.whatWeDoItem3,
    },
    {
      title: 'Decanning & Sample Preparation',
      description: 'The ceramic or metallic catalyst is removed from the shell.',
      image: images.whatWeDoItem4,
    },
    {
      title: 'XRF & Lab Analysis',
      description: 'We test for Platinum (Pt), Palladium (Pd) and Rhodium (Rh).',
      image: images.whatWeDoItem5,
    },
    {
      title: 'Refining & Recovery',
      description: 'Precious metals are extracted using industry-grade refining.',
      image: images.whatWeDoItem1,
    },
    {
      title: 'Market-Based Settlement',
      description: 'You are paid based on real metal content and live PGM prices.',
      image: images.whatWeDoItem2,
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: 'black',
        py: 12,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          height: '70%',
          background: 'radial-gradient(circle, rgba(0, 242, 255, 0.03) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(100px)',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <FadeIn direction="up">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: '#00f2ff', fontWeight: 600, letterSpacing: 2, mb: 2, display: 'block' }}
            >
              OUR PROCESS
            </Typography>
            <Typography
              variant="h2"
              sx={{
                ...gradients.primaryText,
                mb: 3,
                fontWeight: 700,
              }}
            >
              What We Do
            </Typography>
            <Typography
              sx={{
                color: 'grey.400',
                fontSize: '1.25rem',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              From intake to settlement, our streamlined 5-step process ensures maximum value recovery and transparent pricing.
            </Typography>
          </Box>
        </FadeIn>

        {/* Services Grid */}
        <Grid container spacing={3}>
          {services.map((service, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: index < 3 ? 4 : 6 }}>
              <FadeIn delay={index * 0.1} fullWidth>
                <ServiceCard {...service} />
              </FadeIn>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WhatWeDo;
